import React from "react";
import moment from "moment";
import { Post } from "../../../../stores/CommunityStore/posts/types";
import { Topic } from "../../../../stores/CommunityStore/topics/types";

interface SidebarProps {
  onSelect?: () => void;
  onOpenHouseRules: () => void;
  onOpenNotificationSettings: () => void;
  onSelectTopic: (topicName: string, topicUuid?: string) => void;
  onShowTools: () => void;
  selectedTopicName: string;
  isToolsOpen: boolean;
  topics: Topic[];
  featuredPosts: Post[];
  onSelectFeaturedPost: (postUuid: string) => void;
}

const Sidebar = ({
  onSelect,
  onOpenHouseRules,
  onOpenNotificationSettings,
  onSelectTopic,
  onShowTools,
  selectedTopicName,
  isToolsOpen,
  topics,
  featuredPosts,
  onSelectFeaturedPost,
}: SidebarProps) => {
  return (
    <aside
      className="community-shell__sidebar"
      aria-label="Community navigation"
    >
      <div className="community-shell__sidebar-card">
        <h2 className="community-shell__sidebar-title">
          <span className="ic" aria-hidden="true">
            <svg viewBox="0 0 32 32">
              <use href="#ic-chat"></use>
            </svg>
          </span>{" "}
          All topics
        </h2>
        <div className="community-shell__channels" role="navigation">
          {topics.map((topic) => (
            <button
              className={`community-shell__channel ${
                !isToolsOpen && selectedTopicName === topic.name
                  ? "community-shell__channel--active"
                  : ""
              }`}
              type="button"
              onClick={() => {
                onSelectTopic(topic.name, topic.uuid);
                onSelect?.();
              }}
              key={topic.uuid || topic.name}
            >
              <span className="community-shell__channel-name">
                # {topic.name}
              </span>
            </button>
          ))}
        </div>
        <button
          className="community-shell__sidebar-link"
          type="button"
          onClick={onOpenHouseRules}
        >
          <svg className="ic-gold" viewBox="0 0 32 32" aria-hidden="true">
            <use href="#ic-scroll"></use>
          </svg>
          House rules
        </button>
        <button
          className="community-shell__sidebar-link"
          type="button"
          onClick={onOpenNotificationSettings}
        >
          Notification settings
        </button>
      </div>

      <div className="community-shell__sidebar-card">
        <h2 className="community-shell__sidebar-title">
          <svg viewBox="0 0 32 32">
            <use href="#ic-coffee"></use>
          </svg>
          Office Hours
        </h2>

        <p className="community-shell__sidebar-copy">
          Stuck on something? Book a 30-minute chat with us about anything you
          fancy. If we can't help, the thinkers behind our courses usually can.
        </p>
        <a
          className="community-shell__sidebar-cta"
          href="https://calendar.app.google/boUS7vndHa5hvmsJ8"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book a slot &rarr;
        </a>
      </div>

      <div className="community-shell__sidebar-card">
        <h2 className="community-shell__sidebar-title">
          <svg viewBox="0 0 32 32">
            <use href="#ic-sparkle"></use>
          </svg>
          Smart Tools Library
        </h2>
        <p className="community-shell__sidebar-copy">
          Helpful AI tools we create to make you shine. New ones ship most
          weeks.
        </p>
        <button
          className="community-shell__sidebar-cta sidebar-cta-btn"
          type="button"
          aria-pressed={isToolsOpen}
          onClick={() => {
            onShowTools();
            onSelect?.();
          }}
        >
          All tools &rarr;
        </button>
      </div>

      <div className="community-shell__sidebar-card">
        <h2 className="community-shell__sidebar-title">
          <svg viewBox="0 0 32 32">
            <use href="#ic-books"></use>
          </svg>
          Handy Resources
        </h2>
        <p className="community-shell__sidebar-copy">
          Industry reports, decks and swipe files for you to download and steal
          from.
        </p>
        <a
          className="community-shell__sidebar-cta"
          href="https://drive.google.com/drive/folders/1gKhK0bAzyMlXaztPIFZ12OddtZ4yL0tU?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
        >
          Browse resources &rarr;
        </a>
      </div>

      <div className="community-shell__sidebar-card">
        <h2 className="community-shell__sidebar-title">
          <svg viewBox="0 0 32 32">
            <use href="#ic-flame"></use>
          </svg>
          Featured Posts
        </h2>
        <div className="community-shell__featured-list">
          {featuredPosts.slice(0, 3).map((post) => (
            <button
              className="community-shell__featured-post"
              type="button"
              onClick={() => {
                onSelectFeaturedPost(post.uuid);
                onSelect?.();
              }}
              key={post.uuid}
            >
              <div
                className="community-shell__featured-title"
                dangerouslySetInnerHTML={{ __html: post.formatted_content }}
              />
              <div className="community-shell__featured-meta">
                {post.comments_count || 0}{" "}
                {post.comments_count === 1 ? "reply" : "replies"}
                {post.created_at && ` · ${moment(post.created_at).fromNow()}`}
              </div>
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
